import type { ReactNode, SVGProps } from 'react';

/**
 * Icônes de la plateforme, dessinées ici (aucune bibliothèque) : grille de 24, trait fin de 1,5 px
 * (vague 7, avec la police Inter), angles arrondis, couleur héritée (`currentColor`). Toujours
 * décoratives (`aria-hidden`) : le texte voisin, ou un libellé `sr-only`, porte le sens.
 * Une icône sert à lire plus vite (navigation, statut, alerte), jamais à décorer.
 */
type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'> & { size?: number; strokeWidth?: number };

function make(paths: ReactNode, displayName: string) {
  function Icon({ size = 20, strokeWidth = 1.5, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        focusable="false"
        {...rest}
      >
        {paths}
      </svg>
    );
  }
  Icon.displayName = displayName;
  return Icon;
}

export const HomeIcon = make(
  <>
    <path d="M3.5 10.5 12 4l8.5 6.5" />
    <path d="M5.5 9v10.5h13V9" />
    <path d="M10 19.5v-5h4v5" />
  </>,
  'HomeIcon',
);

/** Parc : véhicule vu de face. */
export const CarIcon = make(
  <>
    <path d="M4 16.5V12l2-5.2A2 2 0 0 1 7.9 5.5h8.2a2 2 0 0 1 1.9 1.3L20 12v4.5" />
    <path d="M3 12h18v4.5H3z" />
    <path d="M5 16.5V19h2.5v-2.5M16.5 16.5V19H19v-2.5" />
    <path d="M7 14.2h1.5M15.5 14.2H17" />
  </>,
  'CarIcon',
);

export const FolderIcon = make(
  <>
    <path d="M3.5 7a1.5 1.5 0 0 1 1.5-1.5h4.2l2 2.2H19A1.5 1.5 0 0 1 20.5 9.2v8.3A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5z" />
  </>,
  'FolderIcon',
);

export const PlusIcon = make(<path d="M12 5v14M5 12h14" />, 'PlusIcon');

/** Entrée : véhicule qui entre (flèche vers un cadre). */
export const EntryIcon = make(
  <>
    <path d="M14 4.5h4.5A1.5 1.5 0 0 1 20 6v12a1.5 1.5 0 0 1-1.5 1.5H14" />
    <path d="M4 12h11M11 8l4 4-4 4" />
  </>,
  'EntryIcon',
);

export const UsersIcon = make(
  <>
    <circle cx="9" cy="8.5" r="3.2" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M15.5 5.6a3.2 3.2 0 0 1 0 5.8M17.5 14.2a5.5 5.5 0 0 1 3 4.8" />
  </>,
  'UsersIcon',
);

export const UserIcon = make(
  <>
    <circle cx="12" cy="8.5" r="3.5" />
    <path d="M5 19.5a7 7 0 0 1 14 0" />
  </>,
  'UserIcon',
);

export const ChevronLeftIcon = make(<path d="M14.5 6 8.5 12l6 6" />, 'ChevronLeftIcon');
export const ChevronRightIcon = make(<path d="M9.5 6l6 6-6 6" />, 'ChevronRightIcon');
export const ChevronDownIcon = make(<path d="M6 9.5l6 6 6-6" />, 'ChevronDownIcon');
export const CloseIcon = make(<path d="M6 6l12 12M18 6 6 18" />, 'CloseIcon');
export const CheckIcon = make(<path d="M5 12.5l4.5 4.5L19 7.5" />, 'CheckIcon');
export const SearchIcon = make(
  <>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4 4" />
  </>,
  'SearchIcon',
);
export const AlertIcon = make(
  <>
    <path d="M12 4 2.8 19.5h18.4z" />
    <path d="M12 10v4.2M12 17.2v.1" />
  </>,
  'AlertIcon',
);
export const InfoIcon = make(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5.5M12 7.8v.1" />
  </>,
  'InfoIcon',
);
export const CheckCircleIcon = make(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m8.3 12.3 2.6 2.6 5-5.3" />
  </>,
  'CheckCircleIcon',
);
export const ClockIcon = make(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </>,
  'ClockIcon',
);
export const MessageIcon = make(<path d="M4.5 5.5h15v10.5h-8.5L6.5 19.5V16h-2z" />, 'MessageIcon');
export const PinIcon = make(
  <>
    <path d="M12 20.5s6.5-5.6 6.5-10.5a6.5 6.5 0 0 0-13 0c0 4.9 6.5 10.5 6.5 10.5z" />
    <circle cx="12" cy="10" r="2.3" />
  </>,
  'PinIcon',
);

/** Punaise (03/10/2026) : dossier épinglé par le membre ; à ne pas confondre avec `PinIcon` (emplacement). */
export const PushpinIcon = make(
  <>
    <path d="M9 3.5h6" />
    <path d="M10 3.5v5.2L7 12.5h10l-3-3.8V3.5" />
    <path d="M12 12.5v8" />
  </>,
  'PushpinIcon',
);

export const CameraIcon = make(
  <>
    <path d="M4 8.5h3l1.8-2.5h6.4L17 8.5h3v10H4z" />
    <circle cx="12" cy="13.2" r="3.3" />
  </>,
  'CameraIcon',
);

export const ShareIcon = make(
  <>
    <path d="M12 3.5v11.5M7.5 8 12 3.5 16.5 8" />
    <path d="M5 13v6.5h14V13" />
  </>,
  'ShareIcon',
);

export const PhoneIcon = make(
  <path d="M5.5 4h3.5l1.8 4.5-2.3 1.4a10.5 10.5 0 0 0 5.6 5.6l1.4-2.3 4.5 1.8v3.5a1.5 1.5 0 0 1-1.5 1.5A15.5 15.5 0 0 1 4 5.5 1.5 1.5 0 0 1 5.5 4z" />,
  'PhoneIcon',
);

// ─── Vague 5 : messagerie, compte, console, scan ───────────────────────────

export const BellIcon = make(
  <>
    <path d="M6.5 16.5V11a5.5 5.5 0 0 1 11 0v5.5l1.5 1.5H5z" />
    <path d="M10 20h4" />
  </>,
  'BellIcon',
);
export const ShieldIcon = make(
  <>
    <path d="M12 3.5 5 6v5.5c0 4.2 3 7.6 7 9 4-1.4 7-4.8 7-9V6z" />
    <path d="M9.2 12.2 11.2 14l3.8-4" />
  </>,
  'ShieldIcon',
);
export const PaletteIcon = make(
  <>
    <path d="M12 3.5a8.5 8.5 0 0 0 0 17c1.2 0 1.8-.8 1.8-1.7 0-1.3-1.2-1.6-1.2-2.8 0-1 .8-1.7 1.8-1.7h2.1a4 4 0 0 0 4-4c0-3.8-3.8-6.8-8.5-6.8z" />
    <circle cx="8" cy="11" r="1" />
    <circle cx="11" cy="7.5" r="1" />
    <circle cx="15.5" cy="8.5" r="1" />
  </>,
  'PaletteIcon',
);
export const SlidersIcon = make(
  <>
    <path d="M5 7h9M18 7h1M5 17h3M12 17h7" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </>,
  'SlidersIcon',
);
export const LifebuoyIcon = make(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="m6 6 3.5 3.5M14.5 14.5 18 18M18 6l-3.5 3.5M9.5 14.5 6 18" />
  </>,
  'LifebuoyIcon',
);
export const MailIcon = make(
  <>
    <path d="M3.5 6.5h17v11h-17z" />
    <path d="m4 7 8 6 8-6" />
  </>,
  'MailIcon',
);
export const KeyIcon = make(
  <>
    <circle cx="8" cy="15" r="3.5" />
    <path d="m10.5 12.5 8-8M15.5 7.5l2 2M17.5 5.5l1.5 1.5" />
  </>,
  'KeyIcon',
);
export const BuildingIcon = make(
  <>
    <path d="M4 20V9l8-4.5L20 9v11" />
    <path d="M8 20v-6h8v6M3 20h18" />
  </>,
  'BuildingIcon',
);
export const ScanIcon = make(
  <>
    <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
    <path d="M7 12h10" />
  </>,
  'ScanIcon',
);
export const FlashIcon = make(<path d="M13 3.5 6 13.5h5l-1 7 7-10h-5z" />, 'FlashIcon');
export const LogOutIcon = make(
  <>
    <path d="M14 4.5H6.5v15H14" />
    <path d="M10.5 12h10M17 8.5l3.5 3.5-3.5 3.5" />
  </>,
  'LogOutIcon',
);

// ─── Vague 7 : navigation groupée, étapes du dossier, indicateurs, widgets ──

/** Agenda (module facultatif). */
export const CalendarIcon = make(
  <>
    <path d="M4.5 6.5h15v13h-15z" />
    <path d="M4.5 10.5h15M8.5 4v4M15.5 4v4" />
  </>,
  'CalendarIcon',
);

/** « Plus » de la navigation basse : trois points. */
export const MoreIcon = make(
  <>
    <circle cx="5.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="18.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
  </>,
  'MoreIcon',
);

/** Étape « Réception » : fiche sur porte-bloc. */
export const ClipboardIcon = make(
  <>
    <path d="M9 4.5H6.5v16h11v-16H15" />
    <path d="M9 3.5h6v3H9z" />
    <path d="M9 11.5h6M9 15h4" />
  </>,
  'ClipboardIcon',
);

/** Étape « Diagnostic » : cadran de mesure. */
export const GaugeIcon = make(
  <>
    <path d="M4.2 16.5a8.5 8.5 0 1 1 15.6 0" />
    <path d="m12 13 3.5-4" />
    <circle cx="12" cy="13.5" r="1.2" />
  </>,
  'GaugeIcon',
);

/** Étape « Travaux » : clé plate. */
export const WrenchIcon = make(
  <path d="M14.5 4.2a4.5 4.5 0 0 0-4.8 6L4.5 15.4a1.9 1.9 0 0 0 2.7 2.7l5.2-5.2a4.5 4.5 0 0 0 6-4.8l-2.7 2.7-2.4-.6-.6-2.4z" />,
  'WrenchIcon',
);

/** Étape « Attente pièces » : colis. */
export const PackageIcon = make(
  <>
    <path d="M12 3.5 4 7.5v9l8 4 8-4v-9z" />
    <path d="m4 7.5 8 4 8-4M12 11.5v9M8 5.5l8 4" />
  </>,
  'PackageIcon',
);

/** Étape « Prêt » : drapeau d'arrivée. */
export const FlagIcon = make(
  <>
    <path d="M5.5 20.5v-16" />
    <path d="M5.5 4.5h12l-2.5 4 2.5 4h-12" />
  </>,
  'FlagIcon',
);

/** Décision du client reçue : document validé. */
export const DecisionIcon = make(
  <>
    <path d="M6 3.5h8l4 4v13H6z" />
    <path d="M14 3.5v4h4" />
    <path d="m9 14 2 2 4-4.5" />
  </>,
  'DecisionIcon',
);

/** Question ouverte d'un client : bulle avec point d'interrogation. */
export const QuestionIcon = make(
  <>
    <path d="M4.5 5.5h15v10.5h-8.5L6.5 19.5V16h-2z" />
    <path d="M10.2 9.2a1.9 1.9 0 1 1 2.6 1.8c-.5.2-.8.6-.8 1.1v.3M12 14.1v.1" />
  </>,
  'QuestionIcon',
);

/** Modification demandée par le client : crayon. */
export const EditIcon = make(
  <>
    <path d="M15.5 5 19 8.5 9 18.5H5.5V15z" />
    <path d="m13 7.5 3.5 3.5" />
  </>,
  'EditIcon',
);

/** Grille de widgets (Accueil personnalisable). */
export const GridIcon = make(
  <>
    <path d="M4.5 4.5h6v6h-6zM13.5 4.5h6v6h-6zM4.5 13.5h6v6h-6zM13.5 13.5h6v6h-6z" />
  </>,
  'GridIcon',
);

/** Ouvrir en pleine page (vue liste + détail). */
export const ExpandIcon = make(
  <>
    <path d="M14 4.5h5.5V10M10 19.5H4.5V14" />
    <path d="M19.5 4.5 13.5 10.5M4.5 19.5l6-6" />
  </>,
  'ExpandIcon',
);

/** Jours à l'atelier : sablier. */
export const HourglassIcon = make(
  <>
    <path d="M7 3.5h10M7 20.5h10" />
    <path d="M8 3.5c0 4.5 4 5.5 4 8.5s-4 4-4 8.5M16 3.5c0 4.5-4 5.5-4 8.5s4 4 4 8.5" />
  </>,
  'HourglassIcon',
);
